import styled from '@emotion/styled'
import { AdmStackOutput } from '@pubstack/common/src/stack'
import { FunctionComponent } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Chip from '~/components/Chip'
import { IconProps } from '~/components/Icon'
import { Tooltip } from '~/components/Tooltip'
import { CHANGELOG_ICONS, CHIP_STYLE_BY_ACTION } from './stackChangelogStyles'
import { useAdmStackChangelog } from './useAdmStackChangelog'
import { TooltipContent } from './ViewStackFlyout'

const SectionTitle = styled.div`
  ${Fonts.P2}
  color: ${Colors.Jet};
  font-weight: 700;
  margin-bottom: 0.25rem;
`

const Flex = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  align-items: center;
`

const DeviceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const DeviceLabel = styled.span`
  ${Fonts.P2}
  font-weight: 500;
`

type DesyncChipProps = {
  text: string
  iconLeft: IconProps
  chipStyle: Record<string, string>
  tooltipTitle: string
  tooltipItems?: string[]
}

const DesyncChip: FunctionComponent<DesyncChipProps> = ({ text, iconLeft, chipStyle, tooltipTitle, tooltipItems }) => {
  const chip = <Chip iconLeft={iconLeft} text={text} css={chipStyle} />
  if (!tooltipItems || tooltipItems.length === 0) return chip
  return (
    <Tooltip
      offset={8}
      title={
        <TooltipContent css={{ width: 'fit-content', maxWidth: '40rem' }}>
          <div>
            <SectionTitle>{tooltipTitle}</SectionTitle>
            <Flex>
              {tooltipItems.map((item) => (
                <Chip key={item} text={item} color={Colors.Ghost} />
              ))}
            </Flex>
          </div>
        </TooltipContent>
      }
    >
      {chip}
    </Tooltip>
  )
}

type Props = {
  output1: AdmStackOutput | undefined
  output2: AdmStackOutput | undefined
}

export const AdmStackChangelog: FunctionComponent<Props> = ({ output1, output2 }) => {
  const { changelog } = useAdmStackChangelog(output1, output2)

  const devices = [
    { key: 'desktop', label: 'Desktop', deviceChangelog: changelog.desktop },
    { key: 'mobile', label: 'Mobile', deviceChangelog: changelog.mobile },
  ] as const

  return (
    <>
      {devices.map(({ key, label, deviceChangelog }) => {
        const hasChanges =
          deviceChangelog.adUnits.added.length > 0 ||
          deviceChangelog.adUnits.removed.length > 0 ||
          deviceChangelog.adUnits.changes.length > 0 ||
          Object.keys(deviceChangelog.bidders.added).length > 0 ||
          Object.keys(deviceChangelog.bidders.changed).length > 0 ||
          Object.keys(deviceChangelog.bidders.removed).length > 0 ||
          deviceChangelog.prebidConfig.added.length > 0 ||
          deviceChangelog.prebidConfig.changed.length > 0 ||
          deviceChangelog.prebidConfig.removed.length > 0 ||
          deviceChangelog.floors.changes.length > 0 ||
          deviceChangelog.refreshRules.changes.length > 0 ||
          deviceChangelog.lazyLoading.changes.length > 0 ||
          deviceChangelog.gamConfig.changes.length > 0

        if (!hasChanges) return null

        return (
          <DeviceSection key={key}>
            <DeviceLabel>{label}</DeviceLabel>
            <Flex>
              {deviceChangelog.adUnits.added.map((name) => (
                <DesyncChip key={`adunit-added-${name}`} text={name} iconLeft={CHANGELOG_ICONS['added']} chipStyle={CHIP_STYLE_BY_ACTION['added']} tooltipTitle="" />
              ))}
              {deviceChangelog.adUnits.removed.map((name) => (
                <DesyncChip key={`adunit-removed-${name}`} text={name} iconLeft={CHANGELOG_ICONS['removed']} chipStyle={CHIP_STYLE_BY_ACTION['removed']} tooltipTitle="" />
              ))}
              {deviceChangelog.adUnits.changes.length > 0 && (
                <DesyncChip
                  iconLeft={CHANGELOG_ICONS['changed']}
                  text="Ad units"
                  chipStyle={CHIP_STYLE_BY_ACTION['changed']}
                  tooltipTitle="Changed ad units"
                  tooltipItems={deviceChangelog.adUnits.changes}
                />
              )}
              {Object.entries(deviceChangelog.bidders.added).map(([bidder, { adUnitNames }]) => (
                <DesyncChip
                  key={`bidder-added-${bidder}`}
                  text={bidder}
                  iconLeft={CHANGELOG_ICONS['added']}
                  chipStyle={CHIP_STYLE_BY_ACTION['added']}
                  tooltipTitle="Added mapping"
                  tooltipItems={adUnitNames}
                />
              ))}
              {Object.entries(deviceChangelog.bidders.removed).map(([bidder, { adUnitNames }]) => (
                <DesyncChip
                  key={`bidder-removed-${bidder}`}
                  text={bidder}
                  iconLeft={CHANGELOG_ICONS['removed']}
                  chipStyle={CHIP_STYLE_BY_ACTION['removed']}
                  tooltipTitle="Removed mapping"
                  tooltipItems={adUnitNames}
                />
              ))}
              {Object.entries(deviceChangelog.bidders.changed).map(([bidder, { adUnitNames }]) => (
                <DesyncChip
                  key={`bidder-changed-${bidder}`}
                  text={bidder}
                  iconLeft={CHANGELOG_ICONS['changed']}
                  chipStyle={CHIP_STYLE_BY_ACTION['changed']}
                  tooltipTitle="Changed mapping"
                  tooltipItems={adUnitNames}
                />
              ))}
              {deviceChangelog.prebidConfig.added.map((field) => (
                <DesyncChip key={`prebid-added-${field}`} text={field} iconLeft={CHANGELOG_ICONS['added']} chipStyle={CHIP_STYLE_BY_ACTION['added']} tooltipTitle="" />
              ))}
              {deviceChangelog.prebidConfig.removed.map((field) => (
                <DesyncChip key={`prebid-removed-${field}`} text={field} iconLeft={CHANGELOG_ICONS['removed']} chipStyle={CHIP_STYLE_BY_ACTION['removed']} tooltipTitle="" />
              ))}
              {deviceChangelog.prebidConfig.changed.map((field) => (
                <DesyncChip key={`prebid-changed-${field}`} text={field} iconLeft={CHANGELOG_ICONS['changed']} chipStyle={CHIP_STYLE_BY_ACTION['changed']} tooltipTitle="" />
              ))}
              {deviceChangelog.floors.changes.length > 0 && (
                <DesyncChip
                  iconLeft={CHANGELOG_ICONS['changed']}
                  text="Floor rules"
                  chipStyle={CHIP_STYLE_BY_ACTION['changed']}
                  tooltipTitle="Changed floor rules"
                  tooltipItems={deviceChangelog.floors.changes}
                />
              )}
              {deviceChangelog.refreshRules.changes.length > 0 && (
                <DesyncChip
                  iconLeft={CHANGELOG_ICONS['changed']}
                  text="Refresh rules"
                  chipStyle={CHIP_STYLE_BY_ACTION['changed']}
                  tooltipTitle="Changed refresh rules"
                  tooltipItems={deviceChangelog.refreshRules.changes}
                />
              )}
              {deviceChangelog.lazyLoading.changes.length > 0 && (
                <DesyncChip
                  iconLeft={CHANGELOG_ICONS['changed']}
                  text="Lazy loading"
                  chipStyle={CHIP_STYLE_BY_ACTION['changed']}
                  tooltipTitle="Changed lazy loading"
                  tooltipItems={deviceChangelog.lazyLoading.changes}
                />
              )}
              {deviceChangelog.gamConfig.changes.length > 0 && (
                <DesyncChip
                  iconLeft={CHANGELOG_ICONS['changed']}
                  text="GAM config"
                  chipStyle={CHIP_STYLE_BY_ACTION['changed']}
                  tooltipTitle="Changed GAM config"
                  tooltipItems={deviceChangelog.gamConfig.changes}
                />
              )}
            </Flex>
          </DeviceSection>
        )
      })}
    </>
  )
}
